import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BottomSheet from "./BottomSheet";
import Icon from "./Icon";
import analyticsService from "../../services/analyticsService";
import { LANGUAGES, rememberLanguage, supportedLanguages } from "../../languages";

const isWebLayout = () => Boolean(window.matchMedia?.("(min-width: 768px)").matches);

// Choix de la langue (écran 1f) : feuille du bas sur mobile, menu déroulant sous
// le bouton en version web. Seules les langues traduites sont proposées.
export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState(null); // null | "sheet" | "menu"
  const containerRef = useRef(null);
  const available = LANGUAGES.filter((lang) => supportedLanguages().includes(lang.code));
  const current = available.find((lang) => i18n.language?.startsWith(lang.code)) || available[0];

  // Le menu se ferme au clic en dehors ou sur Échap.
  useEffect(() => {
    if (mode !== "menu") return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setMode(null);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMode(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mode]);

  const choose = (code) => {
    setMode(null);
    if (code === current.code) return;
    i18n.changeLanguage(code);
    rememberLanguage(code);
    analyticsService.pushEvent("language_changed", { language: code });
  };

  const options = (rowClasses) => (
    <ul className="flex flex-col gap-0.5" role="listbox" aria-label={t("language.title")}>
      {available.map((lang) => {
        const selected = lang.code === current.code;
        return (
          <li key={lang.code}>
            <button
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => choose(lang.code)}
              className={`w-full flex items-center gap-3 text-left text-[15px] ${rowClasses(selected)}`}
            >
              <Icon name={`flag-${lang.flag}`} size={22} />
              <span className="flex-1">{lang.label}</span>
              {lang.isNew && (
                <span className="rounded-full bg-qv-brand-100 px-2 py-0.5 text-[11px] font-bold text-qv-brand-900">
                  {t("language.new")}
                </span>
              )}
              {selected && <Icon name="check-read-linear" size={18} className="text-qv-brand" />}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setMode(mode ? null : isWebLayout() ? "menu" : "sheet")}
        className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold ${
          mode === "menu"
            ? "border-[1.5px] border-qv-brand bg-qv-brand-50 text-qv-brand"
            : "border border-qv-line-strong bg-white hover:border-qv-brand"
        }`}
        aria-haspopup={isWebLayout() ? "listbox" : "dialog"}
        aria-expanded={Boolean(mode)}
        aria-label={t("language.title")}
      >
        <Icon name={`flag-${current.flag}`} size={18} />
        {current.code.toUpperCase()}
        <Icon name={mode === "menu" ? "alt-arrow-up-linear" : "alt-arrow-down-linear"} size={14} />
      </button>

      {mode === "menu" && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-30 w-[280px] rounded-xl border border-qv-line bg-white p-2 shadow-[0_12px_16px_-4px_rgba(10,13,18,.08),0_4px_6px_-2px_rgba(10,13,18,.03)]">
          {options((selected) =>
            `rounded-lg px-3 py-2.5 ${selected ? "bg-qv-brand-50 font-semibold" : "font-medium hover:bg-qv-canvas"}`
          )}
        </div>
      )}

      <BottomSheet open={mode === "sheet"} onClose={() => setMode(null)} title={t("language.title")}>
        <div className="mt-3">
          {options((selected) =>
            `rounded-xl px-3.5 py-3 ${selected ? "border border-qv-brand bg-qv-brand-50 font-semibold" : "border border-transparent hover:bg-qv-canvas"}`
          )}
        </div>
      </BottomSheet>
    </div>
  );
}
